import React, { useState, useRef, useEffect } from 'react';
import { ContentRow, TextStyle } from '../../_types/invitation-design-v2';
import { HexColorPicker } from 'react-colorful';
import FontSelector from './FontSelector';
import { AVAILABLE_FONTS } from '../../_types/invitation-design';

interface InlineElementControlsProps {
  selectedRow: ContentRow;
  theme: any;
  stylePresets: any;
  position: { top: number; left: number; width: number; height: number };
  containerHeight?: number;
  onUpdateRow: (rowId: string, updates: Partial<ContentRow>) => void;
  onUpdateRowStyle: (rowId: string, styleUpdates: Partial<TextStyle>) => void;
  onAddRow: (afterId?: string) => void;
  onRemoveRow: (rowId: string) => void;
  onMoveRow: (rowId: string, direction: 'up' | 'down') => void;
}

const InlineElementControls: React.FC<InlineElementControlsProps> = ({
  selectedRow,
  theme,
  stylePresets,
  position,
  containerHeight = 800,
  onUpdateRow,
  onUpdateRowStyle,
  onAddRow,
  onRemoveRow,
  onMoveRow,
}) => {
  const [activeControl, setActiveControl] = useState<string | null>(null);
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hoveredControl, setHoveredControl] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const presetButtonRef = useRef<HTMLButtonElement>(null);

  const currentStyle = selectedRow.style || {};
  
  // Get the preset style if one is selected
  const presetStyle = selectedRow.preset && stylePresets[selectedRow.preset] ? stylePresets[selectedRow.preset] : {};
  
  // Helper to get actual value being used (override > preset > theme default)
  const getActualValue = (property: keyof TextStyle, defaultValue?: any) => {
    if (currentStyle[property] !== undefined) {
      return currentStyle[property];
    }
    if (presetStyle[property] !== undefined) {
      return presetStyle[property];
    }
    if (theme[property] !== undefined) {
      return theme[property];
    }
    return defaultValue;
  };
  
  // Helper to resolve template variables to actual values
  const resolveTemplateVariable = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    
    // Replace template variables with actual theme values
    return value
      .replace('{{template.theme.primaryColor}}', theme.primaryColor || '')
      .replace('{{template.theme.secondaryColor}}', theme.secondaryColor || '')
      .replace('{{template.theme.textColor}}', theme.textColor || '')
      .replace('{{template.theme.accentColor}}', theme.accentColor || '')
      .replace('{{template.theme.fontFamily}}', theme.fontFamily || '')
      .replace('{{template.theme.secondaryFontFamily}}', theme.secondaryFontFamily || '');
  };
  
  // Get the actual color value (resolved from template if needed)
  const getActualColor = (): string => {
    const colorValue = getActualValue('color', theme.textColor);
    return resolveTemplateVariable(colorValue);
  };
  
  // Check if a style property is overridden from the preset
  const isStyleOverridden = (property: keyof TextStyle): boolean => {
    if (!selectedRow.preset) return false;
    
    // Get the actual current value (from currentStyle or default)
    const currentValue = currentStyle[property];
    const presetValue = presetStyle[property];
    
    // If there's a value in currentStyle, compare it with preset
    if (currentValue !== undefined) {
      return currentValue !== presetValue;
    }
    
    // If no custom value, it's not overridden
    return false;
  };
  
  // Check if there are any style overrides
  const hasAnyOverrides = (): boolean => {
    if (!selectedRow.preset || !currentStyle) return false;
    
    // Check all possible style properties
    const styleProperties: (keyof TextStyle)[] = [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textTransform', 
      'textAlign', 'lineHeight', 'letterSpacing', 'wordSpacing', 'marginBottom', 'color'
    ];
    
    return styleProperties.some(prop => isStyleOverridden(prop));
  };
  
  // Calculate available space for preset selector
  const spaceAbove = position.top;
  const spaceBelow = containerHeight - (position.top + position.height);
  const presetSelectorAbove = spaceBelow < 300 && spaceAbove > 300;
  const dropdownHeight = Math.min(400, Math.max(spaceAbove, spaceBelow) - 50);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (controlsRef.current && !controlsRef.current.contains(target)) {
        setActiveControl(null);
        setShowPresetDropdown(false);
        setShowColorDropdown(false);
        setShowColorPicker(false);
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Reset state when selected row changes
  useEffect(() => {
    setActiveControl(null);
    setShowPresetDropdown(false);
    setShowColorDropdown(false);
    setShowColorPicker(false);
    setIsEditing(false);
    setHoveredControl(null);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, [selectedRow.id]);
  
  // Clear hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Typography control icons with their controls
  const typographyControls = [
    {
      id: 'fontFamily',
      properties: ['fontFamily'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M9.62 12L12 5.67 14.37 12M11 3L5.5 18h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2z"/>
        </svg>
      ),
      label: 'Font Family',
      control: (
        <div className="p-3 w-80">
          <FontSelector
            label="Font Family"
            value={(() => {
              const fontValue = currentStyle.fontFamily !== undefined 
                ? currentStyle.fontFamily 
                : (presetStyle.fontFamily || theme.fontFamily || 'Playfair Display');
              
              // If it's a template variable, return it as-is
              if (fontValue.includes('{{template.theme.')) {
                return fontValue;
              }
              // Otherwise extract just the font name
              return fontValue.split(',')[0].trim();
            })()}
            onChange={(fontFamily) => {
              // Check if it's a template variable
              if (fontFamily.includes('{{template.theme.')) {
                onUpdateRowStyle(selectedRow.id, { fontFamily });
                setActiveControl(null);
              } else {
                // Get the full font family value from AVAILABLE_FONTS
                const fonts = Object.values(AVAILABLE_FONTS);
                const selectedFont = fonts.find(f => f.family === fontFamily);
                if (selectedFont) {
                  onUpdateRowStyle(selectedRow.id, { 
                    fontFamily: `${selectedFont.family}, ${selectedFont.category}` 
                  });
                  setActiveControl(null);
                }
              }
            }}
            placeholder="Choose a font..."
            themeFonts={{
              primary: theme.fontFamily,
              secondary: theme.secondaryFontFamily
            }}
          />
        </div>
      )
    },
    {
      id: 'fontSize',
      properties: ['fontSize'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.246 15H4.754l-2 5H.6L7 4h2l6.4 16h-2.154l-2-5zm-.8-2L8 6.885 5.554 13h4.892zM22 15h-5l-1 3h-1.5L18 8h1.5l3.5 10h-1.5l-1-3zm-.75-1.5L19.5 9l-1.75 4.5h3.5z"/>
        </svg>
      ),
      label: 'Font Size',
      control: (
        <div className="p-3 w-64">
          <label className="text-xs text-gray-600 mb-2 block">Font Size</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={getActualValue('fontSize', 16)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { fontSize: Number(e.target.value) })}
              min="8"
              max="72"
              step="1"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <input
              type="number"
              value={getActualValue('fontSize', 16)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { fontSize: Number(e.target.value) })}
              className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
              min="8"
              max="120"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>
      )
    },
    {
      id: 'fontWeight',
      properties: ['fontWeight'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
        </svg>
      ),
      label: 'Font Weight',
      control: (
        <div className="p-3 w-48">
          <label className="text-xs text-gray-600 mb-2 block">Font Weight</label>
          <select
            value={getActualValue('fontWeight', 'normal')}
            onChange={(e) => {
              onUpdateRowStyle(selectedRow.id, { fontWeight: e.target.value });
              setActiveControl(null);
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="300">Light</option>
            <option value="normal">Normal</option>
            <option value="500">Medium</option>
            <option value="600">Semibold</option>
            <option value="bold">Bold</option>
            <option value="900">Black</option>
          </select>
        </div>
      )
    },
    {
      id: 'fontStyle',
      properties: ['fontStyle', 'textTransform'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
        </svg>
      ),
      label: 'Font Style',
      control: (
        <div className="p-3">
          <label className="text-xs text-gray-600 mb-2 block">Font Style</label>
          <div className="flex gap-2">
            <button
              onClick={() => onUpdateRowStyle(selectedRow.id, { 
                fontStyle: getActualValue('fontStyle', 'normal') === 'italic' ? 'normal' : 'italic' 
              })}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                getActualValue('fontStyle', 'normal') === 'italic'
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <span className="italic">Italic</span>
            </button>
            <button
              onClick={() => onUpdateRowStyle(selectedRow.id, { 
                textTransform: getActualValue('textTransform', 'none') === 'uppercase' ? 'none' : 'uppercase' 
              })}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                getActualValue('textTransform', 'none') === 'uppercase'
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              ABC
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'textAlign',
      properties: ['textAlign'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z"/>
        </svg>
      ),
      label: 'Text Alignment',
      control: (
        <div className="p-3">
          <label className="text-xs text-gray-600 mb-2 block">Text Alignment</label>
          <div className="flex gap-1">
            {['left', 'center', 'right'].map((align) => (
              <button
                key={align}
                onClick={() => onUpdateRowStyle(selectedRow.id, { textAlign: align as any })}
                className={`px-3 py-2 text-xs rounded-lg transition-colors ${
                  getActualValue('textAlign', 'center') === align
                    ? 'bg-rose-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {align.charAt(0).toUpperCase() + align.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'lineHeight',
      properties: ['lineHeight'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 7l3-3 3 3H8v10h3l-3 3-3-3h3V7z"/>
          <path d="M15 6h2.5l4 12h-2.5l-.8-2.5h-3.4l-.8 2.5h-2.5L15 6zm1.25 7.5h2.5L17.5 9.5h-.5l-1.25 4z"/>
        </svg>
      ),
      label: 'Line Height',
      control: (
        <div className="p-3 w-64">
          <label className="text-xs text-gray-600 mb-2 block">Line Height</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={getActualValue('lineHeight', 1.6)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { lineHeight: Number(e.target.value) })}
              min="0.8"
              max="3"
              step="0.1"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <input
              type="number"
              value={getActualValue('lineHeight', 1.6)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { lineHeight: Number(e.target.value) })}
              className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
              min="0.8"
              max="3"
              step="0.1"
            />
            <span className="text-xs text-gray-500">x</span>
          </div>
        </div>
      )
    },
    {
      id: 'letterSpacing',
      properties: ['letterSpacing'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 3v18H2V3h20m0-2H2C.9 1 0 1.9 0 3v18c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2z"/>
          <path d="M4 12l2-2v1.5h3v1H6V14l-2-2zm16 0l-2 2v-1.5h-3v-1h3V10l2 2z"/>
          <path d="M13.4 15H10.6l-.5 1.5H8.5L11.3 9h1.4l2.8 7.5H14l-.6-1.5zm-.5-1.3L12 11l-.9 2.7h1.8z"/>
        </svg>
      ),
      label: 'Letter Spacing',
      control: (
        <div className="p-3 w-64">
          <label className="text-xs text-gray-600 mb-2 block">Letter Spacing</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={getActualValue('letterSpacing', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { letterSpacing: Number(e.target.value) })}
              min="-5"
              max="10"
              step="0.5"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <input
              type="number"
              value={getActualValue('letterSpacing', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { letterSpacing: Number(e.target.value) })}
              className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
              min="-5"
              max="10"
              step="0.5"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>
      )
    },
    {
      id: 'wordSpacing',
      properties: ['wordSpacing'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 5h18v2H3z"/>
          <path d="M8 10l-2 2 2 2v-1.5h2v-1H8z"/>
          <path d="M16 10v1.5h-2v1h2V14l2-2z"/>
          <path d="M3 17h18v2H3z"/>
        </svg>
      ),
      label: 'Word Spacing',
      control: (
        <div className="p-3 w-64">
          <label className="text-xs text-gray-600 mb-2 block">Word Spacing</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={getActualValue('wordSpacing', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { wordSpacing: Number(e.target.value) })}
              min="-5"
              max="20"
              step="1"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <input
              type="number"
              value={getActualValue('wordSpacing', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { wordSpacing: Number(e.target.value) })}
              className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
              min="-5"
              max="20"
              step="1"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>
      )
    },
    {
      id: 'marginBottom',
      properties: ['marginBottom'] as (keyof TextStyle)[],
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 8h18v2H3z"/>
          <path d="M11 10h2v7h-2z"/>
          <path d="M12 20l-4-4h3v-3h2v3h3z"/>
        </svg>
      ),
      label: 'Bottom Margin',
      control: (
        <div className="p-3 w-64">
          <label className="text-xs text-gray-600 mb-2 block">Bottom Margin</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              value={getActualValue('marginBottom', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { marginBottom: Number(e.target.value) })}
              min="0"
              max="100"
              step="1"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
            />
            <input
              type="number"
              value={getActualValue('marginBottom', 0)}
              onChange={(e) => onUpdateRowStyle(selectedRow.id, { marginBottom: Number(e.target.value) })}
              className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
              min="0"
              max="100"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>
      )
    },
    {
      id: 'color',
      properties: ['color'] as (keyof TextStyle)[],
      icon: (
        <div className="w-4 h-4 rounded-full border border-gray-300" 
          style={{ backgroundColor: getActualColor() }} 
        />
      ),
      label: 'Color',
      control: (
        <div className="p-2 w-56">
          <div className="grid grid-cols-2 gap-1 mb-2">
            <button
              onClick={() => {
                onUpdateRowStyle(selectedRow.id, { color: undefined });
                setActiveControl(null);
              }}
              className={`flex items-center gap-1.5 p-1.5 rounded hover:bg-gray-50 transition-colors ${
                currentStyle.color === undefined && presetStyle.color === undefined ? 'bg-rose-50' : ''
              }`}
              title="Theme Default"
            >
              <div 
                className="w-4 h-4 rounded border border-gray-300" 
                style={{ backgroundColor: theme.textColor }}
              />
              <span className="text-xs">Default</span>
            </button>
            {[
              { name: 'Primary', key: 'primaryColor', color: theme.primaryColor, template: '{{template.theme.primaryColor}}' },
              { name: 'Secondary', key: 'secondaryColor', color: theme.secondaryColor, template: '{{template.theme.secondaryColor}}' },
              { name: 'Text', key: 'textColor', color: theme.textColor, template: '{{template.theme.textColor}}' },
              { name: 'Accent', key: 'accentColor', color: theme.accentColor, template: '{{template.theme.accentColor}}' },
            ].map((themeColor) => {
              const currentColorValue = getActualValue('color', theme.textColor);
              const isSelected = currentColorValue === themeColor.color || currentColorValue === themeColor.template;
              
              return (
                <button
                  key={themeColor.key}
                  onClick={() => {
                    onUpdateRowStyle(selectedRow.id, { color: themeColor.color });
                    setActiveControl(null);
                  }}
                  className={`flex items-center gap-1.5 p-1.5 rounded hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-rose-50' : ''
                  }`}
                  title={themeColor.name}
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: themeColor.color }}
                  />
                  <span className="text-xs">{themeColor.name}</span>
                </button>
              );
            })}
          </div>
          <details className="group">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Custom color</summary>
            <div className="mt-2">
              <HexColorPicker
                color={getActualColor()}
                onChange={(color) => onUpdateRowStyle(selectedRow.id, { color })}
              />
            </div>
          </details>
        </div>
      )
    }
  ];

  return (
    <div 
      ref={controlsRef}
      className="inline-element-controls absolute pointer-events-none"
      style={{
        top: position.top - 4,
        left: position.left - 4,
        width: position.width + 8,
        height: position.height + 8,
        zIndex: 9999,
      }}
    >
      {/* Selection outline */}
      <div className="absolute inset-0 border-2 border-rose-500 rounded-lg" />
      
      {/* Left side - Style selector and edit button centered vertically */}
      {selectedRow.type === 'text' && (
        <div className="absolute top-1/2 transform -translate-y-1/2 pointer-events-auto" style={{ right: 'calc(100% + 10px)' }}>
          <div className="flex items-center gap-1">
            {/* Edit button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(!isEditing);
              }}
              className={`p-1.5 border rounded shadow-sm cursor-pointer transition-colors ${
                hasAnyOverrides()
                  ? 'bg-blue-500 border-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-white border-gray-300 hover:bg-gray-50'
              } ${
                isEditing 
                  ? hasAnyOverrides() 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'bg-gray-50 border-gray-400'
                  : ''
              }`}
              title={isEditing ? "Hide editing controls" : "Show editing controls"}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" 
                />
              </svg>
            </button>
            
            {/* Style preset selector */}
            <div className="relative">
              <button
                ref={presetButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPresetDropdown(!showPresetDropdown);
                  setActiveControl(null);
                }}
                className={`px-2 py-1 bg-white border rounded shadow-sm cursor-pointer text-xs font-medium transition-colors ${
                  hasAnyOverrides()
                    ? 'border-blue-500 hover:bg-blue-50'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                title={
                  hasAnyOverrides()
                    ? 'Style preset (with custom overrides)'
                    : 'Change style preset'
                }
              >
                <span className={hasAnyOverrides() ? 'text-blue-600' : ''}>
                  {selectedRow.preset || 'Custom'}
                </span>
              </button>

              {/* Preset dropdown */}
              {showPresetDropdown && (
                <div 
                  className={`absolute left-full ml-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden ${
                    presetSelectorAbove ? 'bottom-0' : 'top-0'
                  }`}
                  style={{ maxHeight: `${dropdownHeight}px`, overflowY: 'auto' }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateRow(selectedRow.id, { preset: undefined, style: undefined });
                      setShowPresetDropdown(false);
                    }}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      !selectedRow.preset ? 'bg-rose-50' : ''
                    }`}
                  >
                    <div className="text-xl font-bold text-gray-700">Aa</div>
                    <div className="text-left flex-1">
                      <div className="font-medium">Custom Style</div>
                      <div className="text-xs text-gray-500">No preset</div>
                    </div>
                  </button>
                  
                  <div className="border-t border-gray-100" />
                  
                  {Object.entries(stylePresets).map(([key, preset]: [string, any]) => (
                    <button
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateRow(selectedRow.id, { preset: key, style: undefined });
                        setShowPresetDropdown(false);
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        selectedRow.preset === key ? 'bg-rose-50' : ''
                      }`}
                    >
                      <div 
                        className="text-xl font-bold"
                        style={{
                          fontSize: `${Math.min(preset.fontSize * 0.8, 20)}px`,
                          fontWeight: preset.fontWeight || 'normal',
                          fontStyle: preset.fontStyle || 'normal',
                          letterSpacing: preset.letterSpacing ? `${preset.letterSpacing * 0.5}px` : 'normal',
                          textTransform: preset.textTransform || 'none',
                          color: preset.color || '#3A3A3A'
                        }}
                      >
                        Aa
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-medium capitalize">{key}</div>
                        <div className="text-xs text-gray-500">
                          {preset.fontSize}px, {preset.fontWeight || 'normal'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Typography controls - show if editing mode OR has overrides */}
          {(isEditing || hasAnyOverrides()) && (
            <div className={`absolute left-0 ${presetSelectorAbove ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
              <div className={`flex flex-col gap-1 ${presetSelectorAbove ? 'flex-col-reverse' : ''}`}>
                {typographyControls.map((control) => (
              <div 
                key={control.id} 
                className="relative"
                onMouseEnter={() => {
                  if (!activeControl && hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  if (!activeControl) {
                    setHoveredControl(control.id);
                  }
                }}
                onMouseLeave={() => {
                  if (activeControl !== control.id) {
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredControl(null);
                    }, 300);
                  }
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveControl(activeControl === control.id ? null : control.id);
                    setShowPresetDropdown(false);
                  }}
                  className={`w-8 h-8 p-1.5 bg-white border rounded shadow-sm transition-colors flex items-center justify-center ${
                    control.properties.some(prop => isStyleOverridden(prop))
                      ? 'border-blue-500 hover:bg-blue-50'
                      : 'border-gray-300 hover:bg-gray-50'
                  } ${
                    activeControl === control.id || hoveredControl === control.id 
                      ? control.properties.some(prop => isStyleOverridden(prop))
                        ? 'bg-blue-50 border-blue-600'
                        : 'bg-gray-50 border-gray-400'
                      : ''
                  }`}
                  title={control.label}
                >
                  <span className={control.properties.some(prop => isStyleOverridden(prop)) ? 'text-blue-600' : ''}>
                    {control.icon}
                  </span>
                </button>
                
                {/* Chevron indicator */}
                {(activeControl === control.id || hoveredControl === control.id) && (
                  <div className="absolute left-full ml-1 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
                
                {(activeControl === control.id || hoveredControl === control.id) && (
                  <div 
                    className={`absolute left-full ml-6 bg-white border border-gray-200 rounded-lg shadow-xl z-50`}
                    style={{
                      ...(control.id === 'color' 
                        ? { top: '-100px' }
                        : presetSelectorAbove 
                          ? { bottom: 0 } 
                          : { top: 0 }
                      )
                    }}
                    onMouseEnter={() => {
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                      }
                      setHoveredControl(control.id);
                    }}
                    onMouseLeave={() => {
                      hoverTimeoutRef.current = setTimeout(() => {
                        setHoveredControl(null);
                      }, 300);
                    }}
                  >
                    {control.control}
                  </div>
                )}
                  </div>
                ))}
                
                {/* Reset button - only show if there are overrides */}
                {hasAnyOverrides() && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateRow(selectedRow.id, { style: undefined });
                      setIsEditing(false);
                    }}
                    className="px-3 py-1.5 bg-white border border-red-300 rounded shadow-sm text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-400 transition-colors"
                    title="Reset to preset defaults"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Top controls - Delete and Move */}
      <div className="absolute -top-10 right-0 flex items-center gap-1 pointer-events-auto">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMoveRow(selectedRow.id, 'up');
          }}
          className="p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm cursor-pointer"
          title="Move up"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMoveRow(selectedRow.id, 'down');
          }}
          className="p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm"
          title="Move down"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveRow(selectedRow.id);
          }}
          className="p-1.5 bg-white border border-gray-300 rounded hover:bg-red-50 hover:text-red-600 shadow-sm ml-2"
          title="Delete"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>



      {/* Divider controls - simpler edit button */}
      {selectedRow.type === 'divider' && (
        <div className="absolute -left-10 top-1/2 transform -translate-y-1/2 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(!isEditing);
            }}
            className={`p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm cursor-pointer flex items-center gap-1 ${
              isEditing ? 'bg-gray-50 border-gray-400' : ''
            }`}
            title={isEditing ? "Hide controls" : "Show controls"}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d={isEditing 
                  ? "M19 9l-7 7-7-7" // chevron down when open
                  : "M9 5l7 7-7 7"   // chevron right when closed
                } 
              />
            </svg>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" 
              />
            </svg>
          </button>
          
          {isEditing && (
            <div className={`absolute left-0 ${presetSelectorAbove ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
          <div className={`flex flex-col gap-1 ${presetSelectorAbove ? 'flex-col-reverse' : ''}`}>
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveControl(activeControl === 'dividerWidth' ? null : 'dividerWidth');
              }}
              className={`p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm ${
                activeControl === 'dividerWidth' ? 'bg-gray-50 border-gray-400' : ''
              }`}
              title="Width"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" transform="rotate(90 12 12)" />
              </svg>
            </button>
            
            {activeControl === 'dividerWidth' && (
              <div className={`absolute left-full ml-2 ${
                presetSelectorAbove ? 'bottom-0' : 'top-0'
              } bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-48 z-50`}>
                <label className="text-xs text-gray-600 mb-2 block">Width</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    value={currentStyle.width || 100}
                    onChange={(e) => onUpdateRowStyle(selectedRow.id, { width: Number(e.target.value) })}
                    min="10"
                    max="300"
                    step="1"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <input
                    type="number"
                    value={currentStyle.width || 100}
                    onChange={(e) => onUpdateRowStyle(selectedRow.id, { width: Number(e.target.value) })}
                    className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
                    min="10"
                    max="300"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveControl(activeControl === 'dividerHeight' ? null : 'dividerHeight');
              }}
              className={`p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm ${
                activeControl === 'dividerHeight' ? 'bg-gray-50 border-gray-400' : ''
              }`}
              title="Height"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
              </svg>
            </button>
            
            {activeControl === 'dividerHeight' && (
              <div className={`absolute left-full ml-2 ${
                presetSelectorAbove ? 'bottom-0' : 'top-0'
              } bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-48 z-50`}>
                <label className="text-xs text-gray-600 mb-2 block">Height</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    value={currentStyle.height || 1}
                    onChange={(e) => onUpdateRowStyle(selectedRow.id, { height: Number(e.target.value) })}
                    min="1"
                    max="10"
                    step="1"
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-500"
                  />
                  <input
                    type="number"
                    value={currentStyle.height || 1}
                    onChange={(e) => onUpdateRowStyle(selectedRow.id, { height: Number(e.target.value) })}
                    className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveControl(activeControl === 'dividerColor' ? null : 'dividerColor');
              }}
              className={`p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm ${
                activeControl === 'dividerColor' ? 'bg-gray-50 border-gray-400' : ''
              }`}
              title="Color"
            >
              <div className="w-3 h-3 rounded-full border border-gray-400" 
                style={{ backgroundColor: currentStyle.backgroundColor || theme.primaryColor }} 
              />
            </button>
            
            {activeControl === 'dividerColor' && (
              <div className={`absolute left-full ml-2 ${
                presetSelectorAbove ? 'bottom-0' : 'top-0'
              } bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64 z-50`}>
                <div className="text-xs text-gray-600 mb-2">Color</div>
                <HexColorPicker
                  color={currentStyle.backgroundColor || theme.primaryColor}
                  onChange={(color) => onUpdateRowStyle(selectedRow.id, { backgroundColor: color })}
                />
              </div>
            )}
          </div>
          </div>
          </div>
        )}
      </div>
      )}

      {/* Bottom right - Add button */}
      <div className="absolute -bottom-10 right-0 pointer-events-auto">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddRow(selectedRow.id);
          }}
          className="p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 shadow-sm"
          title="Add element below"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default InlineElementControls;